import React, {useCallback, useState} from 'react';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
	Typography,
	Box,
	CircularProgress,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

export type ShareLinkDialogProps = {
	open: boolean;
	onClose: () => void;
	title: string;
	description: string;
	privateHint: string;
	/** Empty while there is no share key. */
	shareLink: string;
	isLoading: boolean;
	isGenerating: boolean;
	isDeleting: boolean;
	busy: boolean;
	onGenerate: () => void;
	onRevoke: () => void;
};

/** Secret-link dialog shared by every shareable resource; the wrapper owns the resource's access key queries. */
const ShareLinkDialog = ({open, onClose, title, description, privateHint, shareLink, isLoading, isGenerating, isDeleting, busy, onGenerate, onRevoke}: ShareLinkDialogProps) => {
	const [copyFeedback, setCopyFeedback] = useState<string | null>(null);

	const handleCopyLink = useCallback(async () => {
		if (!shareLink) {
			return;
		}
		try {
			await navigator.clipboard.writeText(shareLink);
			setCopyFeedback('Link copied');
			setTimeout(() => setCopyFeedback(null), 2000);
		} catch {
			setCopyFeedback('Could not copy');
			setTimeout(() => setCopyFeedback(null), 2000);
		}
	}, [shareLink]);

	return (
		<Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
			<DialogTitle>
				{title}
			</DialogTitle>
			<DialogContent>
				{isLoading ? (
					<Box display='flex' justifyContent='center' py={3}>
						<CircularProgress size={32} />
					</Box>
				) : (
					<>
						<Typography variant='body2' color='text.secondary' paragraph>
							{description}
						</Typography>
						{shareLink ? (
							<>
								<TextField
									label='Share link'
									value={shareLink}
									fullWidth
									multiline
									minRows={2}
									size='small'
									InputProps={{readOnly: true}}
									sx={{mb: 1}}
								/>
								<Box display='flex' alignItems='center' gap={1} flexWrap='wrap'>
									<Button
										startIcon={<ContentCopyIcon />}
										variant='outlined'
										onClick={handleCopyLink}
										disabled={busy}
									>
										Copy link
									</Button>
									{copyFeedback && (
										<Typography variant='caption' color='text.secondary'>
											{copyFeedback}
										</Typography>
									)}
								</Box>
							</>
						) : (
							<Typography variant='body2' color='text.secondary'>
								{privateHint}
							</Typography>
						)}
					</>
				)}
			</DialogContent>
			<DialogActions sx={{px: 3, pb: 2}}>
				<Button onClick={onClose}>Close</Button>
				{shareLink ? (
					<Button color='error' onClick={onRevoke} disabled={busy}>
						{isDeleting ? <CircularProgress size={20} /> : 'Revoke link'}
					</Button>
				) : (
					<Button variant='contained' onClick={onGenerate} disabled={busy}>
						{isGenerating ? <CircularProgress size={20} color='inherit' /> : 'Generate link'}
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};

export default ShareLinkDialog;
